import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import LanguageMenu from "@/components/LanguageMenu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { House, Buildings, Trophy, Sword, CalendarCheck, ChartBar, MonitorPlay, SignOut, List, UserCircle, CreditCard, Translate, UserPlus, GearSix, UsersThree, IdentificationCard, Users, ChatCircle, ChatCircleDots, ShoppingBag, Storefront, SquaresFour, Monitor, ChartLine, CurrencyDollar, Broadcast, Broom, Brain, Medal, GameController, Robot, Tag, Heartbeat, GraduationCap, ShieldStar, Coin, Megaphone, X, CodeSimple, Kanban, Lock, LockKey, PersonSimpleRun, FilePdf, ArrowCounterClockwise, Receipt, PencilSimple, Check, GlobeHemisphereWest, TShirt, SoccerBall, ShieldCheck, BookOpenText, Atom, Palette, HandHeart } from "@phosphor-icons/react";
import MusicPlayer from "@/components/MusicPlayer";
import { PadelCenterBadge } from "@/components/PadelCenterLogo";
import { usePadelCenter } from "@/context/PadelCenterContext";
import { useState, useMemo, useEffect } from "react";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import DydMascotBubble from "@/components/DydMascotBubble";
import MandatoryPhotoGate from "@/components/MandatoryPhotoGate";
import { DydWhiteRemovalFilter } from "@/components/Dyd";
import { startUsageTracker, trackPage, stopUsageTracker } from "@/lib/usageTracker";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function UserPhoto({ path, size = 28 }) {
  if (!path) return null;
  return <img src={`${API_URL}/api/users/photo/${path}`} alt="" className="object-cover border border-[#CCFF00]/50" style={{ width: size, height: size }} />;
}

function DydBalancePill({ size = "sm" }) {
  const [balance, setBalance] = useState(null);
  const load = async () => {
    try {
      const r = await api.get("/dyd-coin/wallet");
      setBalance(r.data?.balance ?? 0);
    } catch (e) { /* ignore unauth */ }
  };
  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("dyd-coin-refresh", handler);
    const id = setInterval(load, 60000);
    return () => { window.removeEventListener("dyd-coin-refresh", handler); clearInterval(id); };
  }, []);
  if (balance == null) return null;
  const fmt = new Intl.NumberFormat("es-MX").format(Math.round(balance));
  const big = size === "lg";
  return (
    <Link
      to="/dashboard/dyd-coin"
      className={`group inline-flex items-center gap-1.5 ${big ? "px-3 py-1.5 text-sm" : "px-2 py-1 text-xs"} bg-[#CCFF00]/10 hover:bg-[#CCFF00]/20 border border-[#CCFF00]/40 rounded-full transition-colors`}
      data-testid="dyd-balance-pill"
      title="Mi wallet DYD"
    >
      <Coin size={big ? 18 : 14} weight="fill" className="text-[#CCFF00] group-hover:rotate-12 transition-transform" />
      <span className="font-['Barlow_Condensed'] font-black text-[#CCFF00] leading-none tracking-wide">{fmt}</span>
      <span className="text-[9px] uppercase tracking-widest font-bold text-[#CCFF00]/70">DYD</span>
    </Link>
  );
}

function AnnouncementBanner() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dyd_ann_dismissed") || "[]"); } catch { return []; }
  });
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/announcements/active");
        if (alive) setItems(r.data?.announcements || []);
      } catch (e) { /* ignore */ }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  const visible = items.filter((a) => !dismissed.includes(a.id));
  if (visible.length === 0) return null;
  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try { localStorage.setItem("dyd_ann_dismissed", JSON.stringify(next)); } catch (_err) { /* ignore */ }
  };
  const colors = {
    info: "bg-blue-500/10 border-blue-500/30 text-blue-200",
    warn: "bg-yellow-500/10 border-yellow-500/30 text-yellow-200",
    success: "bg-green-500/10 border-green-500/30 text-green-200",
    promo: "bg-[#CCFF00]/10 border-[#CCFF00]/40 text-[#CCFF00]",
  };
  return (
    <div className="space-y-1 px-4 md:px-6 lg:px-8 pt-3" data-testid="announcement-banners">
      {visible.map((a) => (
        <div key={a.id} className={`flex items-center justify-between gap-2 border px-3 py-2 rounded text-xs ${colors[a.type] || colors.info}`} data-testid={`ann-banner-${a.id}`}>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Megaphone size={14} weight="fill" />
            {a.url ? <a href={a.url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate font-semibold">{a.message}</a> : <span className="truncate font-semibold">{a.message}</span>}
          </div>
          <button onClick={() => dismiss(a.id)} className="opacity-60 hover:opacity-100 p-1" aria-label="Cerrar" data-testid={`ann-dismiss-${a.id}`}>
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

const LOGO = "https://customer-assets.emergentagent.com/job_padel-hub-12/artifacts/ky94ukp9_BB57C119-9264-4E6D-8C4E-D647A6C282EA.png";

// Fallback labels when i18n has no translation for a nav key.
const DEFAULT_NAV_LABELS = {
  atomo: "Átomo",
  dashboard_theme: "Tema Dashboard",
  menu_layout: "Layout Menú",
  partner_match: "Partner Match",
  comunidad_live: "Comunidad Live",
};

const navKeys = [
  { path: "/dashboard", key: "inicio", icon: House, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/clubes", key: "clubes", icon: Buildings, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/torneos", key: "torneos", icon: Trophy, roles: ["admin", "directivo", "profesor", "jugador"], section: "torneos" },
  { path: "/dashboard/retas", key: "retas", icon: Sword, roles: ["admin", "directivo", "profesor", "jugador"], section: "retas" },
  { path: "/dashboard/partner-match", key: "partner_match", icon: HandHeart, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/comunidad-live", key: "comunidad_live", icon: Broadcast, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/clinicas", key: "clinicas", icon: GraduationCap, roles: ["admin", "directivo", "profesor", "jugador"], section: "clinicas" },
  { path: "/dashboard/atomo", key: "atomo", icon: Atom, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/marcador", key: "marcador", icon: Broadcast, roles: ["admin", "directivo", "profesor", "jugador"], section: "marcadores" },
  { path: "/dashboard/jugadores", key: "jugadores", icon: Users, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/comunidad", key: "comunidad", icon: UsersThree, roles: ["admin", "directivo", "profesor", "jugador"], section: "comunidad" },
  { path: "/dashboard/comic", key: "comic", icon: BookOpenText, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/uso-plataforma", key: "uso_plataforma", icon: ChartLine, roles: ["admin", "directivo"] },
  { path: "/dashboard/chat", key: "chat", icon: ChatCircle, roles: ["admin", "directivo", "profesor", "jugador"], section: "chat" },
  { path: "/dashboard/tienda", key: "tienda", icon: ShoppingBag, roles: ["admin", "directivo", "profesor", "jugador"], section: "tienda" },
  { path: "/dashboard/canchas", key: "canchas", icon: CalendarCheck, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/estadisticas", key: "estadisticas", icon: ChartBar, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/streaming", key: "streaming", icon: MonitorPlay, roles: ["admin", "directivo"] },
  { path: "/dashboard/pagos", key: "pagos", icon: CreditCard, roles: ["admin", "directivo", "jugador"] },
  { path: "/dashboard/invitaciones", key: "invitaciones", icon: UserPlus, roles: ["admin", "directivo"] },
  { path: "/dashboard/config-pagos", key: "config_pagos", icon: GearSix, roles: ["admin"] },
  { path: "/dashboard/ingresos", key: "ingresos", icon: CurrencyDollar, roles: ["admin", "directivo"] },
  { path: "/dashboard/usuarios", key: "usuarios", icon: UsersThree, roles: ["admin"] },
  { path: "/dashboard/mapa-jugadores", key: "mapa_jugadores", icon: GlobeHemisphereWest, roles: ["admin"], section: "mapa" },
  { path: "/dashboard/limpiar-historial", key: "limpiar_historial", icon: Broom, roles: ["admin"] },
  { path: "/dashboard/trivia", key: "trivia", icon: Brain, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/atari", key: "atari", icon: GameController, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/padel-game", key: "padel_game", icon: GameController, roles: ["admin", "directivo", "profesor", "jugador"], featureFlag: "padel_game" },
  { path: "/dashboard/admin-flags", key: "admin_flags", icon: GearSix, roles: ["admin"] },
  { path: "/dashboard/admin-gear", key: "admin_gear", icon: TShirt, roles: ["admin"] },
  { path: "/dashboard/balonpie", key: "balonpie", icon: SoccerBall, roles: ["admin", "directivo", "profesor", "jugador"], section: "balonpie" },
  { path: "/dashboard/admin-visibility", key: "admin_visibility", icon: ShieldCheck, roles: ["admin"] },
  { path: "/dashboard/admin-access", key: "admin_access", icon: LockKey, roles: ["admin"] },
  { path: "/dashboard/publicidad", key: "publicidad", icon: Megaphone, roles: ["admin"] },
  { path: "/dashboard/dyd-run", key: "dyd_run", icon: PersonSimpleRun, roles: ["admin", "directivo", "profesor", "jugador"], featureFlag: "dyd_run" },
  { path: "/dashboard/reportes", key: "reportes", icon: FilePdf, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/recuperacion", key: "recuperacion", icon: ArrowCounterClockwise, roles: ["admin"] },
  { path: "/dashboard/dyd-coach", key: "dyd_coach", icon: Robot, roles: ["admin"] },
  { path: "/dashboard/dyd-coin", key: "dyd_coin", icon: Coin, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/plataforma", key: "plataforma", icon: GearSix, roles: ["admin"] },
  { path: "/dashboard/dev", key: "dev", icon: Kanban, roles: ["admin", "desarrollador"] },
  { path: "/dashboard/dyd-access", key: "dyd_access", icon: ShieldStar, roles: ["admin"] },
  { path: "/dashboard/planes", key: "planes", icon: Tag, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/pagos-manuales", key: "pagos_manuales", icon: Receipt, roles: ["admin", "directivo"] },
  { path: "/dashboard/wearables", key: "wearables", icon: Heartbeat, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/ligas", key: "ligas", icon: Medal, roles: ["admin", "directivo", "profesor", "jugador"], section: "ligas" },
  { path: "/dashboard/mi-perfil", key: "mi_perfil", icon: IdentificationCard, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/agenda", key: "agenda", icon: CalendarCheck, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/mi-tarjeta", key: "mi_tarjeta", icon: IdentificationCard, roles: ["admin", "directivo", "profesor", "jugador"] },
  { path: "/dashboard/menu-layout", key: "menu_layout", icon: GearSix, roles: ["admin"] },
  { path: "/dashboard/admin/dashboard-theme", key: "dashboard_theme", icon: Palette, roles: ["admin"] },
];

function SidebarContent({ location, onLogout, user, onNavigate, t, lang, toggleLang }) {
  const { pc } = usePadelCenter();
  const [visibilityMap, setVisibilityMap] = useState(null);
  const [menuLayout, setMenuLayout] = useState(null);
  useEffect(() => {
    if (!user || user.role === "admin") return;
    api.get("/visibility/me").then(({ data }) => setVisibilityMap(data)).catch(() => setVisibilityMap({}));
  }, [user]);
  useEffect(() => {
    if (!user?.role) return;
    api.get(`/menu-layout/${user.role}`).then(({ data }) => setMenuLayout(data)).catch(() => setMenuLayout({ items: [] }));
  }, [user?.role]);
  const visibleItems = useMemo(
    () => {
      const base = navKeys.filter((item) => {
        // Desarrollador: SOLO ve Dev Workspace por default (admin puede ampliar acceso vía dyd_access.granted_modules)
        if (user?.role === "desarrollador") {
          if (item.key === "dev") return true;
          const granted = user?.dyd_access?.granted_modules || [];
          return granted.includes(item.key);
        }
        // DYD Coach: admin OR users with dyd_access.coach flag
        if (item.key === "dyd_coach") {
          if (user?.role === "admin") return true;
          return !!(user?.dyd_access?.coach);
        }
        // Feature visibility matrix — hides items where admin set the section to "none" for this role
        if (item.section && user?.role !== "admin") {
          const level = visibilityMap?.[item.section];
          if (level === "none") return false;
        }
        return item.roles.includes(user?.role);
      });
      // Apply admin-configured menu layout override
      const overrides = new Map((menuLayout?.items || []).map((it) => [it.key, it]));
      if (overrides.size === 0) return base;
      const filtered = base.filter((it) => {
        const o = overrides.get(it.key);
        if (o && o.visible === false) return false;
        return true;
      });
      // Sort by order override
      return filtered.slice().sort((a, b) => {
        const oa = overrides.get(a.key);
        const ob = overrides.get(b.key);
        const orderA = oa && Number.isFinite(oa.order) ? oa.order : 9999;
        const orderB = ob && Number.isFinite(ob.order) ? ob.order : 9999;
        return orderA - orderB;
      });
    },
    [user?.role, user?.dyd_access, visibilityMap, menuLayout]
  );
  const showPC = pc?.is_member || user?.role === "admin";
  return (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-white/10">
        <Link to="/dashboard" className="flex items-center gap-3" onClick={onNavigate}>
          <img src={LOGO} alt="Logo" className="h-8 w-8 rounded-full" />
          <span className="font-['Barlow_Condensed'] font-black text-sm uppercase tracking-wider text-white">DyD Padel</span>
        </Link>
        <ClubContextBadge />
      </div>
      <nav className="flex-1 p-3 space-y-1" data-testid="sidebar-nav">
        {showPC && (
          <Link
            to="/dashboard/padel-center"
            onClick={onNavigate}
            className={`flex items-center gap-3 px-4 py-3 text-sm font-semibold uppercase tracking-wider transition-all duration-200 border-l-2 mb-1 ${
              location.pathname === "/dashboard/padel-center"
                ? "bg-[#CCFF00]/15 text-[#CCFF00] border-[#CCFF00]"
                : "text-[#CCFF00]/80 bg-[#CCFF00]/[0.04] hover:bg-[#CCFF00]/10 border-[#CCFF00]/40"
            }`}
            data-testid="nav-padel-center"
          >
            <Lock size={16} weight="fill" />
            <span className="flex-1">Padel Center</span>
            <PadelCenterBadge size={18} />
          </Link>
        )}
        {visibleItems.map((item) => {
          const active = location.pathname === item.path;
          const override = (menuLayout?.items || []).find((it) => it.key === item.key);
          const translated = t(`nav.${item.key}`);
          const label = override?.label_override || (translated === `nav.${item.key}` ? (DEFAULT_NAV_LABELS[item.key] || item.key) : translated);
          return (
            <Link key={item.path} to={item.path} onClick={onNavigate}
              className={`flex items-center gap-3 px-4 py-3 text-sm font-semibold uppercase tracking-wider transition-all duration-200 ${
                active ? "bg-[#CCFF00]/10 text-[#CCFF00] border-l-2 border-[#CCFF00]" : "text-gray-400 hover:text-[#CCFF00] hover:bg-white/5 border-l-2 border-transparent"
              }`}
              data-testid={`nav-${item.key}`}
            >
              <item.icon size={20} weight={active ? "bold" : "duotone"} />
              {label}
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="mb-3 flex justify-center">
          <DydBalancePill size="lg" />
        </div>
        <LanguageMenu />
        <div className="flex items-center gap-3 px-3 py-2 mb-3">
          {user?.photo_path ? (
            <UserPhoto path={user.photo_path} size={28} />
          ) : (
            <UserCircle size={28} weight="duotone" className="text-[#CCFF00]" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 uppercase tracking-wider">
              {user?.role === "admin" ? (lang === "en" ? "Administrator" : "Administrador") :
               user?.role === "directivo" ? (lang === "en" ? "Director" : "Directivo") :
               user?.role === "profesor" ? (lang === "en" ? "Coach" : "Profesor") :
               (lang === "en" ? "Player" : "Jugador")}
            </p>
          </div>
        </div>
        <Button variant="ghost" onClick={onLogout} className="w-full justify-start text-gray-400 hover:text-red-400 hover:bg-red-500/10 uppercase text-xs tracking-wider font-semibold" data-testid="logout-btn">
          <SignOut size={18} className="mr-2" /> {t("nav.cerrar_sesion")}
        </Button>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleLogout = async () => {
    stopUsageTracker();
    await logout();
    navigate("/");
  };

  // Start heartbeat tracker once user is authenticated
  useEffect(() => {
    if (!user) return;
    startUsageTracker();
    return () => { /* keep running while auth is alive */ };
  }, [user]);

  // Track every route change
  useEffect(() => {
    if (!user) return;
    trackPage(location.pathname);
  }, [location.pathname, user]);

  // Shortcuts for the mobile bottom tab bar — user customizable.
  // Stored under `dyd:bottom-nav` (array of nav keys, max 4 items). Default: first 4 below.
  const ALL_BOTTOM_TABS = [
    { key: "inicio", path: "/dashboard", icon: House, label: t("nav.inicio") || "Inicio" },
    { key: "clubes", path: "/dashboard/clubes", icon: Buildings, label: t("nav.clubes") || "Clubes" },
    { key: "retas", path: "/dashboard/retas", icon: Sword, label: t("nav.retas") || "Retas" },
    { key: "partner_match", path: "/dashboard/partner-match", icon: HandHeart, label: t("nav.partner_match") || "Partner Match" },
    { key: "comunidad_live", path: "/dashboard/comunidad-live", icon: Broadcast, label: t("nav.comunidad_live") || "Live" },
    { key: "marcador", path: "/dashboard/marcador", icon: ChartBar, label: t("nav.marcador") || "Marcador" },
    { key: "torneos", path: "/dashboard/torneos", icon: Trophy, label: t("nav.torneos") || "Torneos" },
    { key: "clinicas", path: "/dashboard/clinicas", icon: CalendarCheck, label: t("nav.clinicas") || "Clínicas" },
    { key: "atomo", path: "/dashboard/atomo", icon: Atom, label: t("nav.atomo") || "Átomo" },
    { key: "comunidad", path: "/dashboard/comunidad", icon: UsersThree, label: t("nav.comunidad") || "Comunidad" },
    { key: "chat", path: "/dashboard/chat", icon: ChatCircleDots, label: t("nav.chat") || "Chat" },
    { key: "tienda", path: "/dashboard/tienda", icon: Storefront, label: t("nav.tienda") || "Tienda" },
    { key: "canchas", path: "/dashboard/canchas", icon: SquaresFour, label: t("nav.canchas") || "Canchas" },
    { key: "padel_center", path: "/dashboard/padel-center", icon: Monitor, label: t("nav.padel_center") || "Padel Center" },
    { key: "streaming", path: "/dashboard/streaming", icon: Broadcast, label: t("nav.streaming") || "Streaming" },
    { key: "estadisticas", path: "/dashboard/estadisticas", icon: ChartLine, label: t("nav.estadisticas") || "Stats" },
    { key: "planes", path: "/dashboard/planes", icon: Tag, label: t("nav.planes") || "Planes" },
    { key: "dyd_run", path: "/dashboard/dyd-run", icon: PersonSimpleRun, label: t("nav.dyd_run") || "DYD Run" },
    { key: "reportes", path: "/dashboard/reportes", icon: FilePdf, label: t("nav.reportes") || "Reportes" },
  ];

  const DEFAULT_BOTTOM_KEYS = ["inicio", "clubes", "retas", "marcador"];
  const [bottomKeys, setBottomKeys] = useState(() => {
    try {
      const raw = localStorage.getItem("dyd:bottom-nav");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length >= 1) return parsed.slice(0, 4);
      }
    } catch { /* ignore */ }
    return DEFAULT_BOTTOM_KEYS;
  });
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const bottomTabs = bottomKeys
    .map((k) => ALL_BOTTOM_TABS.find((t) => t.key === k))
    .filter(Boolean);

  const saveBottomKeys = (next) => {
    setBottomKeys(next);
    try { localStorage.setItem("dyd:bottom-nav", JSON.stringify(next)); } catch { /* ignore */ }
  };
  const toggleBottomKey = (key) => {
    if (bottomKeys.includes(key)) {
      saveBottomKeys(bottomKeys.filter((k) => k !== key));
    } else if (bottomKeys.length < 4) {
      saveBottomKeys([...bottomKeys, key]);
    }
  };

  return (
    <div className="flex bg-[#0A0A0A] dvh-screen" data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside className="w-64 bg-[#141414] border-r border-white/10 hidden lg:flex flex-col shrink-0" data-testid="desktop-sidebar">
        <SidebarContent location={location} onLogout={handleLogout} user={user} onNavigate={() => {}} t={t} lang={lang} toggleLang={toggleLang} />
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header: avatar (opens bottom drawer) on the left, brand center, balance + notifications right */}
        <header
          className="lg:hidden sticky top-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-4 py-3 ios-safe-top"
          style={{ WebkitBackdropFilter: "blur(16px)" }}
        >
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <button
                className="flex items-center gap-2 h-11 min-w-[44px] active:opacity-70"
                data-testid="mobile-avatar-btn"
                aria-label="Abrir menú"
              >
                {user?.photo_path ? (
                  <UserPhoto path={user.photo_path} size={36} />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-[#CCFF00]/15 border border-[#CCFF00]/40 flex items-center justify-center">
                    <UserCircle size={22} weight="duotone" className="text-[#CCFF00]" />
                  </div>
                )}
              </button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="bg-[#141414] border-white/10 p-0 ios-safe-bottom overflow-y-auto rounded-t-2xl"
              style={{ height: "85vh", maxHeight: "85vh" }}
              data-testid="mobile-bottom-drawer"
            >
              <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mt-3" aria-hidden="true" />
              <SidebarContent location={location} onLogout={handleLogout} user={user} onNavigate={() => setSheetOpen(false)} t={t} lang={lang} toggleLang={toggleLang} />
            </SheetContent>
          </Sheet>
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={LOGO} alt="Logo" className="h-7 w-7 rounded-full" />
            <span className="font-['Barlow_Condensed'] font-bold text-sm uppercase tracking-wider">DyD</span>
          </Link>
          <div className="flex items-center gap-2">
            <DydBalancePill />
            <NotificationBell />
          </div>
        </header>

        {/* Desktop top bar with notification */}
        <div className="hidden lg:flex items-center justify-end gap-3 px-6 py-2 border-b border-white/5">
          <DydBalancePill />
          <NotificationBell />
        </div>

        {/* Page Content */}
        <AnnouncementBanner />
        <main id="page-scroll-root" data-scroll-root="true" className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 sm:px-4 sm:py-5 md:px-6 md:py-6 lg:px-8 lg:py-8 pb-24 lg:pb-8">
          <div className="w-full max-w-full mx-auto">
            <Outlet />
          </div>
        </main>

        {/* Mobile bottom tab bar — user customizable */}
        <nav
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-black/95 backdrop-blur-xl border-t border-white/10 ios-safe-bottom"
          style={{ WebkitBackdropFilter: "blur(16px)" }}
          data-testid="mobile-bottom-nav"
        >
          <div className="grid" style={{ gridTemplateColumns: `repeat(${Math.max(1, bottomTabs.length) + 1}, minmax(0, 1fr))` }}>
            {bottomTabs.map((tab) => {
              const active = location.pathname === tab.path;
              return (
                <Link
                  key={tab.key}
                  to={tab.path}
                  className={`relative flex flex-col items-center justify-center py-2.5 gap-0.5 transition-colors ${active ? "text-[#CCFF00]" : "text-gray-400 hover:text-white active:text-[#CCFF00]"}`}
                  data-testid={`bottom-${tab.key}`}
                >
                  <tab.icon size={22} weight={active ? "fill" : "duotone"} />
                  <span className="text-[10px] font-bold uppercase tracking-wider leading-none">{tab.label}</span>
                  {active && <span className="absolute top-0 h-0.5 w-8 bg-[#CCFF00]" aria-hidden="true" />}
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              onContextMenu={(e) => { e.preventDefault(); setCustomizeOpen(true); }}
              className="flex flex-col items-center justify-center py-2.5 gap-0.5 transition-colors text-gray-400 hover:text-white active:text-[#CCFF00]"
              data-testid="bottom-perfil"
              title="Toca: ver perfil · Mantén: personalizar accesos"
            >
              {user?.photo_path ? (
                <UserPhoto path={user.photo_path} size={22} />
              ) : (
                <UserCircle size={22} weight="duotone" />
              )}
              <span className="text-[10px] font-bold uppercase tracking-wider leading-none">Perfil</span>
            </button>
          </div>
          {/* Floating "Customize" pill — top-right of the bottom nav */}
          <button
            type="button"
            onClick={() => setCustomizeOpen(true)}
            className="absolute -top-3 right-3 h-6 px-2 bg-[#CCFF00] text-black text-[9px] font-bold uppercase tracking-widest rounded-full border border-black shadow-md flex items-center gap-1 hover:scale-105 transition-transform"
            data-testid="bottom-customize-btn"
            aria-label="Personalizar accesos rápidos"
          >
            <PencilSimple size={10} weight="bold" /> Editar
          </button>
        </nav>

        {/* Bottom nav customization dialog (mobile only) */}
        <Sheet open={customizeOpen} onOpenChange={setCustomizeOpen}>
          <SheetContent side="bottom" className="bg-[#141414] border-white/10 max-h-[80vh] overflow-y-auto rounded-t-2xl" data-testid="bottom-customize-dialog">
            <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mt-2 mb-4" aria-hidden="true" />
            <div className="px-4 pb-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#CCFF00] font-semibold mb-1">Personalizar</p>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-black uppercase mb-1">Accesos Rápidos</h2>
              <p className="text-xs text-gray-400 mb-4">Selecciona hasta 4 secciones para mostrar en la barra inferior. La pestaña Perfil siempre está presente.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4" data-testid="bottom-customize-grid">
                {ALL_BOTTOM_TABS.map((tab) => {
                  const selected = bottomKeys.includes(tab.key);
                  const disabled = !selected && bottomKeys.length >= 4;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => !disabled && toggleBottomKey(tab.key)}
                      disabled={disabled}
                      className={`relative flex items-center gap-2 p-3 border text-left transition-all ${selected ? "border-[#CCFF00] bg-[#CCFF00]/10 text-[#CCFF00]" : disabled ? "border-white/5 text-gray-600 opacity-40 cursor-not-allowed" : "border-white/10 text-gray-300 hover:border-white/30"}`}
                      data-testid={`bottom-opt-${tab.key}`}
                    >
                      <tab.icon size={20} weight={selected ? "fill" : "duotone"} />
                      <span className="text-xs font-bold uppercase tracking-wide">{tab.label}</span>
                      {selected && <Check size={12} weight="bold" className="absolute top-1 right-1 text-[#CCFF00]" />}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 text-[10px] uppercase tracking-widest text-gray-500">
                <span data-testid="bottom-customize-count">{bottomKeys.length}/4 seleccionados</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => saveBottomKeys(DEFAULT_BOTTOM_KEYS)} className="border-white/10 text-xs uppercase h-7" data-testid="bottom-customize-reset">
                    Restaurar
                  </Button>
                  <Button size="sm" onClick={() => setCustomizeOpen(false)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] text-xs uppercase font-bold h-7" data-testid="bottom-customize-close">
                    Listo
                  </Button>
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Global DYD assistant: floating mascot bubble + SVG color filter */}
      <DydWhiteRemovalFilter />
      {(user?.role === "admin" || user?.dyd_access?.bot) && <DydMascotBubble />}
      <MusicPlayer isAdmin={user?.role === "admin"} />
      {/* Mandatory photo onboarding — blocks the UI until the user uploads a profile photo. */}
      <MandatoryPhotoGate />
    </div>
  );
}

function ClubContextBadge() {
  const location = useLocation();
  const [club, setClub] = useState(null);

  useEffect(() => {
    const path = location.pathname;
    const m = path.match(/\/(?:dashboard\/)?(?:clubes|c)\/([^/]+)/);
    if (!m) { setClub(null); return undefined; }
    const clubId = m[1];
    let cancelled = false;
    const fetchClub = async () => {
      try {
        const r = await api.get(`/clubs/${clubId}`);
        if (cancelled) return;
        const c = r.data?.club || r.data;
        if (c?.id) setClub({ id: c.id, name: c.name, logo_url: c.logo_url });
      } catch (_e) {
        if (!cancelled) setClub(null);
      }
    };
    fetchClub();
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (!club) return null;
  return (
    <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2" data-testid="sidebar-club-badge">
      {club.logo_url ? (
        <img src={club.logo_url} alt={club.name} className="h-8 w-8 rounded-full object-cover bg-white/5" />
      ) : (
        <div className="h-8 w-8 rounded-full bg-[#CCFF00]/15 flex items-center justify-center text-[#CCFF00] text-xs font-bold">
          {(club.name || "?").slice(0, 2).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-gray-500">Viendo club</div>
        <div className="text-xs font-bold text-white truncate">{club.name}</div>
      </div>
    </div>
  );
}

